import { useMutation } from "@tanstack/react-query";
import { ArrowLeft, ArrowRight, CheckCircle2, KeyRound, LockKeyhole, Mail, ShieldCheck } from "lucide-react";
import { useEffect, useState, type FormEvent } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { Brand } from "../components/layout/Brand";
import { Button } from "../components/ui/Button";
import { useAuth } from "../contexts/AuthContext";
import { authErrorMessage, recoverPassword, registerUser } from "../lib/api";

type Mode = "login" | "register" | "recovery";

export function AuthPage() {
  const { mode: routeMode } = useParams();
  const mode: Mode = routeMode === "register" || routeMode === "recovery" ? routeMode : "login";
  const { credentials, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [terms, setTerms] = useState(false);
  const [gdpr, setGdpr] = useState(false);
  const [website, setWebsite] = useState("");
  const [completed, setCompleted] = useState(false);

  useEffect(() => {
    setCompleted(false);
    setWebsite("");
  }, [mode]);

  const loginMutation = useMutation({
    mutationFn: () => login({ login: email.trim().toLowerCase(), password }),
    onSuccess: () => navigate("/app/servers", { replace: true }),
  });
  const registerMutation = useMutation({
    mutationFn: () => registerUser(email.trim().toLowerCase(), terms, gdpr),
    onSuccess: () => setCompleted(true),
  });
  const recoveryMutation = useMutation({
    mutationFn: () => recoverPassword(email.trim().toLowerCase()),
    onSuccess: () => setCompleted(true),
  });

  if (credentials) return <Navigate to="/app/servers" replace />;

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (mode === "login") loginMutation.mutate();
    else if (mode === "register") {
      if (website.trim()) {
        registerMutation.reset();
        setCompleted(true);
        return;
      }
      registerMutation.mutate();
    }
    else recoveryMutation.mutate();
  };
  const activeMutation = mode === "login" ? loginMutation : mode === "register" ? registerMutation : recoveryMutation;
  const errorId = activeMutation.error ? "auth-form-error" : undefined;
  const resetMutations = () => {
    loginMutation.reset();
    registerMutation.reset();
    recoveryMutation.reset();
  };

  return (
    <main className="auth-page">
      <section className="auth-aside">
        <Link to="/" className="auth-back"><ArrowLeft /> Zpět na hlavní stránku</Link>
        <div className="auth-aside__content">
          <Brand />
          <div className="eyebrow"><span /> Český KVM cloud</div>
          <h1>Virtuální servery.<br /><em>Jednoduše a bezpečně.</em></h1>
          <p>Spravujte virtuální servery, síťová rozhraní a kredit z jednoho přehledného místa.</p>
          <ul><li><ShieldCheck /> KVM virtualizace</li><li><ShieldCheck /> Hosting Linuxových a Unixových VPS</li><li><ShieldCheck /> Zvlášť veřejná a privátní konektivita</li><li><ShieldCheck /> Vzdálená plocha VNC</li><li><ShieldCheck /> Anti-DDoS systém v ceně</li><li><ShieldCheck /> AI supervisor na backendu</li></ul>
        </div>
        <small>Cloudovou platformu provozuje Synesta s.r.o.</small>
      </section>
      <section className="auth-content">
        <div className="auth-mobile-brand"><Brand /><Link to="/" className="auth-mobile-back" aria-label="Zpět na hlavní stránku"><ArrowLeft /></Link></div>
        <div className="auth-card">
          {completed ? (
            <div className="auth-success" aria-live="polite">
              <span><CheckCircle2 /></span>
              <h2>Zkontrolujte svůj e-mail</h2>
              <p>{mode === "register" ? "Na zadanou adresu jsme odeslali aktivační odkaz a přístupové údaje. Po aktivaci se můžete přihlásit." : "Pokud účet existuje, poslali jsme na jeho adresu potvrzovací odkaz. Současné heslo zůstane platné, dokud odkaz neotevřete."}</p>
              <Button variant="primary" className="auth-submit auth-submit--login" onClick={() => { setCompleted(false); navigate("/auth/login"); }} icon={<ArrowRight />}>Přejít k přihlášení</Button>
            </div>
          ) : (
            <>
              <div className="auth-card__heading">
                <span>{mode === "login" ? <KeyRound /> : mode === "register" ? <Mail /> : <LockKeyhole />}</span>
                <small>{mode === "login" ? "Klientská zóna" : mode === "register" ? "Nový zákazník" : "Obnova účtu"}</small>
                <h2>{mode === "login" ? "Přihlášení do cloudu" : mode === "register" ? "Vytvoření účtu" : "Obnovení přístupu"}</h2>
                <p>{mode === "login" ? "Vítejte zpět. Zadejte své přístupové údaje." : mode === "register" ? "Stačí e-mailová adresa. Přístupové údaje vám pošleme e-mailem." : "Pošleme bezpečný potvrzovací odkaz. Nové heslo vznikne až po jeho otevření."}</p>
              </div>
              <form onSubmit={onSubmit} className="form-stack">
                <label className="field"><span>E-mailová adresa</span><div className="input-with-icon"><Mail /><input type="email" required autoComplete="email" placeholder="vas@email.cz" value={email} aria-invalid={Boolean(errorId)} aria-describedby={errorId} onChange={(event) => { setEmail(event.target.value); resetMutations(); }} /></div></label>
                {mode === "register" && <div className="registration-honeypot" aria-hidden="true"><label htmlFor="registration-website">Webová stránka</label><input id="registration-website" name="website" type="text" tabIndex={-1} autoComplete="off" value={website} onChange={(event) => setWebsite(event.target.value)} /></div>}
                {mode === "login" && <label className="field"><span>Heslo</span><div className="input-with-icon"><LockKeyhole /><input type="password" required minLength={8} maxLength={50} pattern="[A-Za-z0-9]+" autoComplete="current-password" placeholder="Vaše heslo" value={password} aria-invalid={Boolean(errorId)} aria-describedby={errorId} onChange={(event) => { setPassword(event.target.value); resetMutations(); }} /></div><small>8–50 znaků, povolená jsou písmena a číslice.</small></label>}
                {mode === "register" && (
                  <div className="consents">
                    <label><input type="checkbox" required checked={terms} onChange={(event) => setTerms(event.target.checked)} /><span>Souhlasím se <a href="/documents/vseobecne_podminky_2024.pdf" target="_blank" rel="noreferrer">všeobecnými podmínkami služby</a>.</span></label>
                    <label><input type="checkbox" required checked={gdpr} onChange={(event) => setGdpr(event.target.checked)} /><span>Souhlasím se zpracováním osobních údajů.</span></label>
                  </div>
                )}
                {activeMutation.error && <div id={errorId} className="form-error" role="alert">{authErrorMessage(activeMutation.error, mode)}</div>}
                <Button type="submit" variant="primary" className={`auth-submit auth-submit--${mode}`} busy={activeMutation.isPending} icon={<ArrowRight />}>
                  {mode === "login" ? "Přihlásit se" : mode === "register" ? "Vytvořit účet" : "Odeslat instrukce"}
                </Button>
              </form>
              <div className="auth-links">
                {mode === "login" ? <><Link to="/auth/recovery">Zapomenuté heslo?</Link><span>Nemáte účet? <Link to="/auth/register">Zaregistrujte se</Link></span></> : <span>Máte účet? <Link to="/auth/login">Přihlaste se</Link></span>}
              </div>
            </>
          )}
        </div>
      </section>
    </main>
  );
}
